import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ECellValueDisplayMode,
  ECellValueType,
  EExcelTableColumnType,
  EExcelTablePaginationType,
  EValidationSeverity,
  ExcelTableColDef,
  RowData,
} from 'components/ui/excel-table/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelTable from 'components/ui/excel-table';
import { ICellRendererParams } from 'ag-grid-community';
import { BinderDeploymentRow } from './types';
import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from 'react-query';
import { BinderDeploymentTableProps } from './types';
import { binderApi, brokerApi, contractApi, reinsuredApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import {
  UpsertContractPayloadDto,
  FilterReinsuredsStatusEnum,
  BindingDataResponseDto,
  FilterBrokersStatusEnum,
  FilterReinsuredsOrderEnum,
  FilterBrokersFieldEnum,
  FilterBrokersOrderEnum,
  BinderFilterQueryDtoStatusEnum,
  ContractResponseDtoContractStatusEnum,
  SubmissionResponseDtoStatusEnum,
} from 'libs/pinnacle-sdk';
import { MINIMUM_SUB_ROWS_PER_LAYER } from '../../constants';
import { validateBinderDeploymentRow } from './forms';
import { handleError } from 'utils/form';
import { isEmpty } from 'lodash';
import { message } from 'antd';
import { ECurrencies } from 'constants/enums';
import { v4 as uuidv4 } from 'uuid';
import { realNumberToPercentage } from 'utils';
import { isContractEditable } from 'utils/submission';
import { MAX_PAGE_SIZE } from 'constants/paginations';

const BinderDeploymentTable: React.FC<BinderDeploymentTableProps> = ({
  submission,
  originalCurrency,
  onErrorChange,
  currency,
}) => {
  const { t } = useTranslation();
  const [expandedLayers, setExpandedLayers] = useState<Record<number, boolean>>(
    {}
  );
  const queryClient = useQueryClient();
  const [localTableData, setLocalTableData] = useState<BinderDeploymentRow[]>(
    []
  );

  const isCurrencyConverted =
    originalCurrency !== ECurrencies.USD && currency !== originalCurrency;

  const {
    data: bindingData,
    isLoading: isLoadingBindingData,
    refetch: refetchBindingData,
  } = useQuery(
    [EQueryKeys.BINDING_DATA, submission.submissionId, currency],
    () =>
      contractApi.getBindingData({
        getBindingDataQueryDto: {
          submissionId: submission.submissionId,
          currency,
        },
      })
  );

  const upsertContractMutation = useMutation(
    (upsertContractPayloadDto: UpsertContractPayloadDto) =>
      contractApi.upsertContract({
        upsertContractPayloadDto,
      }),
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('binderDeployment.upsertContractError'),
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries([
          EQueryKeys.TOTAL_DEPLOYMENT_PER_BINDER,
          submission.submissionId,
        ]);
        refetchBindingData();
      },
    }
  );

  const deleteContractMutation = useMutation(
    (params: { contractId: string }) =>
      contractApi.deleteContract({
        contractId: params.contractId,
      }),
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('binderDeployment.deleteContractError'),
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries([
          EQueryKeys.TOTAL_DEPLOYMENT_PER_BINDER,
          submission.submissionId,
        ]);
        refetchBindingData();
      },
    }
  );

  const toggleExpandLayer = useCallback((layerNum: string | number) => {
    setExpandedLayers((prev) => ({
      ...prev,
      [layerNum]: !prev[Number(layerNum)],
    }));
  }, []);

  const getSubRowCount = useCallback(
    (layerNumber: number) => {
      const subLayers = localTableData?.filter(
        (binding) =>
          binding.layerNumber === layerNumber &&
          !binding.isGroupRow &&
          !binding.isAddRow
      );

      return subLayers?.length || 0;
    },
    [localTableData]
  );

  const canDeleteRow = useCallback(
    (row: BinderDeploymentRow) => {
      if (isViewOnlyMode(row.contractStatus)) {
        return false;
      }
      if (row.isGroupRow || row.isAddRow) return false;

      const layerNumber = row.layerNumber;
      const subRowCount = getSubRowCount(layerNumber);

      return subRowCount > MINIMUM_SUB_ROWS_PER_LAYER;
    },
    [getSubRowCount]
  );

  // Auto-expand all layers when data loads
  useEffect(() => {
    if (bindingData?.length) {
      // Create an object with all layers expanded
      const allExpandedLayers = bindingData.reduce(
        (acc, layer) => {
          acc[layer.layerNumber] = true;
          return acc;
        },
        {} as Record<number, boolean>
      );

      setExpandedLayers(allExpandedLayers);
    }
  }, [bindingData]);

  const generateEmptyRow = useCallback((layer: BindingDataResponseDto) => {
    return {
      layerNumber: layer.layerNumber,
      limitType: layer.limitType,
      limit: parseFloat(layer.limit),
      excess: layer.excess ? parseFloat(layer.excess) : undefined,
      layerPremium: parseFloat(layer.premium),
      totalDeductions: realNumberToPercentage(layer.totalDeductions),
      id: uuidv4(),
    };
  }, []);

  const generateGroupRow = useCallback((layer: BindingDataResponseDto) => {
    return {
      id: String(layer.layerNumber),
      layerNumber: layer.layerNumber,
      limitType: layer.limitType,
      limit: parseFloat(layer.limit),
      excess: layer.excess ? parseFloat(layer.excess) : undefined,
      reinsured: null,
      sourceBroker: null,
      binder: null,
      layerPremium: parseFloat(layer.premium),
      totalDeductions: realNumberToPercentage(layer.totalDeductions),
      binderShareQuoted: realNumberToPercentage(layer.binderShareQuoted),
      shareOfLimitQuoted: layer.shareOfLimitQuoted
        ? parseFloat(layer.shareOfLimitQuoted)
        : undefined,
      shareOfPremiumQuoted: layer.shareOfPremiumQuoted
        ? parseFloat(layer.shareOfPremiumQuoted)
        : undefined,
      isGroupRow: true,
      groupId: layer.layerNumber,
    };
  }, []);

  const {
    data: reinsureds,
    isLoading: isLoadingReinsureds,
    fetchNextPage: fetchNextReinsureds,
    hasNextPage: hasNextReinsureds,
  } = useInfiniteQuery(
    [EQueryKeys.REINSURED_LIST],
    ({ pageParam = 1 }) =>
      reinsuredApi.filterReinsureds({
        status: FilterReinsuredsStatusEnum.Active,
        page: pageParam,
        pageSize: MAX_PAGE_SIZE,
        field: 'name',
        order: FilterReinsuredsOrderEnum.Asc,
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.items.length < MAX_PAGE_SIZE) return undefined;
        return pages.length + 1;
      },
    }
  );

  const {
    data: brokers,
    isLoading: isLoadingBrokers,
    fetchNextPage: fetchNextBrokers,
    hasNextPage: hasNextBrokers,
  } = useInfiniteQuery(
    [EQueryKeys.BROKER_LIST],
    ({ pageParam = 1 }) =>
      brokerApi.filterBrokers({
        status: FilterBrokersStatusEnum.Active,
        page: pageParam,
        pageSize: MAX_PAGE_SIZE,
        field: FilterBrokersFieldEnum.Name,
        order: FilterBrokersOrderEnum.Asc,
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.items.length < MAX_PAGE_SIZE) return undefined;
        return pages.length + 1;
      },
    }
  );

  const {
    data: binders,
    isLoading: isLoadingBinders,
    fetchNextPage: fetchNextBinders,
    hasNextPage: hasNextBinders,
  } = useInfiniteQuery(
    [
      EQueryKeys.BINDER_LIST,
      submission.expiryDate,
      submission.inceptionDate,
      submission.mainRegionAndRiskTerritory,
      submission.lineOfBusiness,
      submission.principalOccupation,
    ],
    ({ pageParam = 1 }) =>
      binderApi.filterBinders({
        binderFilterQueryDto: {
          status: BinderFilterQueryDtoStatusEnum.Active,
          authorityEndDate: submission.inceptionDate,
          authorityStartDate: submission.inceptionDate,
          principalOccupation: submission.principalOccupation,
          businessLine: submission.lineOfBusiness,
          ...(submission.mainRegionAndRiskTerritory
            ? { countries: [submission.mainRegionAndRiskTerritory] }
            : []),
        },
        page: pageParam,
        pageSize: MAX_PAGE_SIZE,
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.items.length < MAX_PAGE_SIZE) return undefined;
        return pages.length + 1;
      },
    }
  );

  const flattenedReinsureds = useMemo(
    () => reinsureds?.pages.flatMap((page) => page.items) || [],
    [reinsureds]
  );

  const flattenedBrokers = useMemo(
    () => brokers?.pages.flatMap((page) => page.items) || [],
    [brokers]
  );

  const flattenedBinders = useMemo(
    () => binders?.pages.flatMap((page) => page.items) || [],
    [binders]
  );

  useEffect(() => {
    if (hasNextReinsureds && !isLoadingReinsureds) {
      fetchNextReinsureds();
    }
  }, [hasNextReinsureds, isLoadingReinsureds, fetchNextReinsureds]);

  useEffect(() => {
    if (hasNextBrokers && !isLoadingBrokers) {
      fetchNextBrokers();
    }
  }, [hasNextBrokers, isLoadingBrokers, fetchNextBrokers]);

  useEffect(() => {
    if (hasNextBinders && !isLoadingBinders) {
      fetchNextBinders();
    }
  }, [hasNextBinders, isLoadingBinders, fetchNextBinders]);

  useEffect(() => {
    const layerGroups: Record<number, BinderDeploymentRow[]> = {};
    const layerSummaries: Record<number, BinderDeploymentRow> = {};

    if (bindingData && !isEmpty(bindingData)) {
      bindingData.forEach((layer) => {
        if (!layerGroups[layer.layerNumber]) {
          layerGroups[layer.layerNumber] = [];

          layerSummaries[layer.layerNumber] = generateGroupRow(layer);
        }

        if (layer.contracts && layer.contracts.length > 0) {
          layer.contracts.forEach((contract) => {
            const layerGroupDataRow = {
              id: contract.contractId,
              layerNumber: contract.layerNumber,
              limitType: contract.limitType,
              limit: parseFloat(contract.limit),
              excess: contract.excess ? parseFloat(contract.excess) : undefined,
              layerPremium: parseFloat(contract.premium),
              totalDeductions: realNumberToPercentage(contract.totalDeductions),
              reinsured: contract.reinsuredId,
              sourceBroker: contract.brokerId,
              binder: contract.binderId,
              binderShareQuoted: realNumberToPercentage(
                contract.binderShareQuoted
              ),
              shareOfLimitQuoted:
                parseFloat(contract.binderShareQuoted) *
                parseFloat(contract.limit),
              shareOfPremiumQuoted:
                parseFloat(contract.binderShareQuoted) *
                parseFloat(contract.premium),
              contractStatus: contract?.contractStatus,
            };

            layerGroups[contract.layerNumber].push(layerGroupDataRow);
          });
        } else {
          layerGroups[layer.layerNumber].push(generateEmptyRow(layer));
        }
      });
    } else {
      setLocalTableData([]);
      return;
    }

    const layerAddRows: Record<number, BinderDeploymentRow> = {};
    Object.keys(layerGroups).forEach((layerNumber) => {
      layerAddRows[Number(layerNumber)] = {
        layerNumber: Number(layerNumber),
        limitType: '',
        isAddRow: true,
        groupId: Number(layerNumber),
      };
    });

    let result: BinderDeploymentRow[] = [];

    const sortedLayerNumbers = Object.keys(layerGroups)
      .map(Number)
      .sort((a, b) => a - b);

    sortedLayerNumbers.forEach((layerNumber) => {
      result.push(layerSummaries[layerNumber]);

      if (expandedLayers[layerNumber]) {
        result = [
          ...result,
          ...layerGroups[layerNumber],
          layerAddRows[layerNumber],
        ];
      }
    });

    setLocalTableData(result);
  }, [bindingData, generateGroupRow, generateEmptyRow, expandedLayers]);

  const handleAddNewRow = useCallback(
    (id: string | number, data: BinderDeploymentRow[]) => {
      const layerNumber = Number(id);

      const layerDetails = bindingData?.find(
        (layer) => layer.layerNumber === layerNumber
      );

      if (!layerDetails) {
        return;
      }

      // Check for strict validation errors only
      const hasStrictErrors = data
        .filter((row) => row.layerNumber === layerNumber)
        .some((row, index) => {
          const errors = validateBinderDeploymentRow(row, index, data, t);
          return errors.some(
            (error) =>
              !error.severity || error.severity === EValidationSeverity.STRICT
          );
        });

      if (hasStrictErrors) {
        message.error(t('excelTable.errorBeforeAddRow'));
        return;
      }

      const lastIndex = localTableData.findLastIndex(
        (row) => row.groupId === layerNumber && row.isAddRow
      );

      if (lastIndex === -1) {
        return;
      }

      const newRow = generateEmptyRow(layerDetails);
      setLocalTableData((prev) => [
        ...prev.slice(0, lastIndex),
        newRow,
        ...prev.slice(lastIndex),
      ]);
    },
    [bindingData, generateEmptyRow, localTableData, t]
  );

  // Handle data changes from the grid
  const handleDataChange = useCallback(
    (params: RowData<BinderDeploymentRow>) => {
      const { data, isError } = params;

      if (!data || isError.strict) return;

      // Find the layer to get layerId
      const layer = bindingData?.find(
        (l) => l.layerNumber === data.layerNumber
      );

      if (layer && submission.submissionId) {
        const payload: UpsertContractPayloadDto = {
          contractId: data.id,
          binderId: data.binder as string,
          reinsuredId: data.reinsured as string,
          brokerId: data.sourceBroker as string,
          layerId: layer.layerId,
          submissionId: submission.submissionId,
          binderShareQuoted: ((data.binderShareQuoted || 0) / 100).toString(),
          rateMovement: undefined,
          originalRate: '0',
        };

        upsertContractMutation.mutate(payload);

        setLocalTableData((prev) =>
          prev.map((row) =>
            row.id === data.id && !row.isAddRow && !row.isGroupRow
              ? { ...row, ...data }
              : row
          )
        );
      }
    },
    [bindingData, submission.submissionId, upsertContractMutation]
  );

  const handleDeleteRow = useCallback(
    (params: RowData<BinderDeploymentRow>) => {
      const { data } = params;

      if (!data) return;

      if (!canDeleteRow(data)) {
        return;
      }

      if (data.id) {
        deleteContractMutation.mutate({
          contractId: data.id,
        });
        const newLocalData = localTableData.filter((row) => row.id !== data.id);
        setLocalTableData(newLocalData);
      }
    },
    [canDeleteRow, deleteContractMutation, localTableData]
  );

  const isViewOnlyMode = useCallback(
    (contractStatus?: ContractResponseDtoContractStatusEnum) => {
      const isNonSubmissionEditable = (
        [
          SubmissionResponseDtoStatusEnum.Complete,
          SubmissionResponseDtoStatusEnum.Cancelled,
          SubmissionResponseDtoStatusEnum.Declined,
        ] as SubmissionResponseDtoStatusEnum[]
      ).includes(submission.status);
      if (isCurrencyConverted || isNonSubmissionEditable) {
        return true;
      }

      if (!contractStatus || isContractEditable(contractStatus)) {
        return false;
      }
      return true;
    },
    [isCurrencyConverted]
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'layerNumber',
        headerName: t('binderDeployment.layer'),
        minWidth: 160,
        flex: 1,
        editable: false,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<BinderDeploymentRow>) => {
          if (!params.data) return '';

          const layerNum = params.data.layerNumber;
          if (!layerNum) return '';

          if (params.data.isGroupRow) {
            return `Layer ${layerNum} Summary`;
          }

          if (params.data.isAddRow) {
            return '';
          }

          return `Layer ${layerNum}`;
        },
      },
      {
        field: 'limitType',
        headerName: t('binderDeployment.limitType'),
        minWidth: 140,
        resizable: true,
        flex: 1,
        editable: false,
        cellRenderer: (params: ICellRendererParams<BinderDeploymentRow>) => {
          if (!params.data) return '';
          return params.value;
        },
      },
      {
        field: 'limit',
        headerName: 'Limit (100%)',
        minWidth: 140,
        resizable: true,
        flex: 1,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
      },
      {
        field: 'excess',
        headerName: 'Excess (100%)',
        minWidth: 140,
        resizable: true,
        flex: 1,
        precision: 0,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        valueFormatter: (params) => {
          if (!params.value) return '';
          return Number(params.value)?.toLocaleString();
        },
      },
      {
        field: 'layerPremium',
        headerName: 'Layer Premium (100%)',
        minWidth: 170,
        resizable: true,
        flex: 1,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
      },
      {
        field: 'totalDeductions',
        headerName: 'Total Deductions %',
        minWidth: 150,
        resizable: true,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: false,
      },
      {
        field: 'reinsured',
        headerName: 'Reinsured',
        minWidth: 180,
        resizable: true,
        flex: 1,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions: flattenedReinsureds?.map((reinsured) => ({
          label: reinsured.name,
          value: reinsured.reinsuredId,
        })),
        editable: (params) => {
          if (isViewOnlyMode(params.data?.contractStatus)) return false;
          return (
            params.data && !params.data.isGroupRow && !params.data.isAddRow
          );
        },
      },
      {
        field: 'sourceBroker',
        headerName: 'Source Broker',
        minWidth: 180,
        resizable: true,
        flex: 1,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions: flattenedBrokers?.map((broker) => ({
          label: broker.name,
          value: broker.brokerId,
        })),
        editable: (params) => {
          if (isViewOnlyMode(params.data?.contractStatus)) return false;
          return (
            params.data && !params.data.isGroupRow && !params.data.isAddRow
          );
        },
      },
      {
        field: 'binder',
        headerName: t('binderDeployment.binder'),
        minWidth: 200,
        resizable: true,
        flex: 1,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions: flattenedBinders?.map((binder) => ({
          label: binder.binderName,
          value: binder.binderId,
        })),
        editable: (params) => {
          if (isViewOnlyMode(params.data?.contractStatus)) return false;
          return (
            params.data && !params.data.isGroupRow && !params.data.isAddRow
          );
        },
      },
      {
        field: 'binderShareQuoted',
        headerName: t('binding.binderShareWritten'),
        minWidth: 170,
        resizable: true,
        flex: 1,
        precision: 4,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: (params) => {
          if (isViewOnlyMode(params.data?.contractStatus)) return false;

          return (
            params.data && !params.data.isGroupRow && !params.data.isAddRow
          );
        },
      },
      {
        field: 'shareOfLimitQuoted',
        headerName: t('binderDeployment.shareOfLimitQuoted'),
        minWidth: 170,
        resizable: true,
        flex: 1,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
        valueGetter: (params) => {
          if (!params.data || params.data.isAddRow) return null;
          if (
            isViewOnlyMode(params.data?.contractStatus) &&
            params.data.shareOfLimitQuoted
          ) {
            return params.data.shareOfLimitQuoted;
          }
          const binderShare = params.data.binderShareQuoted || 0;
          const layerLimit = params.data.limit || 0;
          return (binderShare / 100) * layerLimit;
        },
      },
      {
        field: 'shareOfPremiumQuoted',
        headerName: t('binderDeployment.shareOfPremiumQuoted'),
        minWidth: 190,
        resizable: true,
        flex: 1,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
        valueGetter: (params) => {
          if (!params.data || params.data.isAddRow) return null;
          if (params.data.shareOfPremiumQuoted) {
            return params.data.shareOfPremiumQuoted;
          }
          const binderShare = params.data.binderShareQuoted || 0;
          const layerPremium = params.data.layerPremium || 0;
          return (binderShare / 100) * layerPremium;
        },
      },
    ],
    [t, flattenedReinsureds, flattenedBrokers, flattenedBinders, isViewOnlyMode]
  );
  return (
    <div className="px-3 py-4 bg-white rounded-lg">
      <ExcelTable
        data={localTableData}
        columnDefs={columnDefs}
        pagination={false}
        rowKeyColName="id"
        baseTableHeight={490}
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={false}
        includeAddRow={false}
        includeDeleteCol
        onErrorChange={({ strict }) => {
          onErrorChange?.(strict);
        }}
        loading={
          isLoadingReinsureds ||
          isLoadingBrokers ||
          isLoadingBinders ||
          isLoadingBindingData
        }
        animateRows={true}
        groupConfig={{
          groupField: 'layerNumber',
          expandedGroups: expandedLayers,
          onToggleGroup: toggleExpandLayer,
        }}
        onAddGroupRow={handleAddNewRow}
        validateRow={(row, rowIndex, allRows) =>
          validateBinderDeploymentRow(row, rowIndex, allRows, t)
        }
        onDataChange={handleDataChange}
        paginationType={EExcelTablePaginationType.INFO}
        includeGroupAddRow={!isViewOnlyMode()}
        onDeleteRow={handleDeleteRow}
        canDeleteRow={canDeleteRow}
      />
    </div>
  );
};

export default BinderDeploymentTable;
